import React, { useState, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import api, { fileUrl } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true`;

const formatAgo = (iso) => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  } catch {
    return '';
  }
};

export const CommentDrawer = ({ post, onClose }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    if (!post) return;
    api.get(`/posts/${post.id}/comments`).then(r => setComments(r.data)).catch(() => {});
  }, [post]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { text });
      setComments([data, ...comments]);
      setText('');
    } finally {
      setPosting(false);
    }
  };

  return (
    <AnimatePresence>
      {post && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            onClick={onClose}
            data-testid="comment-drawer-overlay"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30 }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full sm:w-[480px] bg-[#0F0F11] border-l border-white/10 flex flex-col"
            data-testid="comment-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold">Comments</div>
                <div className="text-white font-heading font-bold text-lg truncate max-w-[340px]">{post.title}</div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-[#A1A1AA] hover:text-white"
                data-testid="comment-drawer-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {comments.length === 0 && (
                <div className="text-center text-[#A1A1AA] text-sm py-12">
                  No comments yet. Be the first to vibe.
                </div>
              )}
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                  <img
                    src={c.author?.avatar_path ? fileUrl(c.author.avatar_path) : avatarFallback(c.author?.name)}
                    className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    alt=""
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-white font-bold text-sm font-heading">{c.author?.name}</span>
                      <span className="text-[#A1A1AA] text-xs">@{c.author?.username}</span>
                      <span className="text-[#52525B] text-xs ml-auto">{formatAgo(c.created_at)}</span>
                    </div>
                    <p className="text-white/90 text-sm mt-1 break-words">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="p-5 border-t border-white/10 flex gap-2">
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Drop a vibe..."
                className="input-default flex-1"
                data-testid="comment-input"
              />
              <button
                type="submit"
                disabled={posting || !text.trim()}
                className="btn-pill btn-primary disabled:opacity-50 flex items-center justify-center w-12 h-12 p-0"
                data-testid="comment-submit"
              >
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CommentDrawer;
